/**
 * Defines behavior for sections with tab navigation.
 *
 * Uses SwiperJS to make tab navigation draggable on small screens.
 */
import dom from '../helpers/dom';
import divi from '../helpers/divi';

const tabNavigation = (tabSections) => {
  const tabNavModuleClass = 'js-role-tabs';
  const tabNavItemClass = 'js-qusd-tab';
  const tabNavItemLinkClass = 'js-qusd-tab-link';
  const tabNavItemActiveClass = 'qusd-tab--active';
  const tabContentWrapperClass = 'js-tabs-content';
  const tabContentItemClass = 'js-tab-content';
  const tabContentItemVisibleClass = 'visible';

  const isTabNavModule = (tabSection) => tabSection.classList.contains(tabNavModuleClass);

  const isTabNavItemActive = (tabNavItem) => tabNavItem.classList.contains(tabNavItemActiveClass);

  const hasActiveTabNavItem = (tabNavItems) => tabNavItems.some(
    (tabNavItem) => isTabNavItemActive(tabNavItem),
  );

  const moveTabContentToWrapper = (tabNavItems, tabContentWrapper) => {
    const tabContentItems = dom.getByClassName(tabContentItemClass);
    const tabNavItemIds = tabNavItems.map(
      (tabNavItem) => tabNavItem.getAttribute('data-content-id'),
    );
    tabContentItems.forEach((tabContentItem) => {
      const tabContentItemId = tabContentItem.getAttribute('id');
      if (tabNavItemIds.includes(tabContentItemId)) {
        tabContentWrapper.appendChild(tabContentItem);
      }
    });
  };

  const selectTab = (selectedTabNavItem, tabNavItems, tabContentWrapper) => {
    const tabContentId = selectedTabNavItem.getAttribute('data-content-id');
    const tabContentItems = dom.getByClassName(tabContentItemClass, tabContentWrapper);
    const selectedTabContentItem = document.getElementById(tabContentId);
    tabNavItems.forEach(
      (tabNavItem) => tabNavItem.classList.remove(tabNavItemActiveClass),
    );
    selectedTabNavItem.classList.add(tabNavItemActiveClass);
    // Can't use `styles.hideElement` here because the class that sets visibility is set with Divi.
    tabContentItems.forEach(
      (tabContentItem) => tabContentItem.classList.remove(tabContentItemVisibleClass),
    );
    selectedTabContentItem.classList.add(tabContentItemVisibleClass);
  };

  const onTabNavItemClick = (event, currentTabNavItem, tabNavItems, tabContentWrapper) => {
    event.preventDefault();
    if (!isTabNavItemActive(currentTabNavItem)) {
      selectTab(currentTabNavItem, tabNavItems, tabContentWrapper);
    }
  };

  const maybeSelectFirstTab = (tabNavItems, tabContentWrapper) => {
    if (!hasActiveTabNavItem(tabNavItems) && tabNavItems.length > 0) {
      const firstTabNavItem = tabNavItems[0];
      selectTab(firstTabNavItem, tabNavItems, tabContentWrapper);
    }
  };

  const addEventListeners = (tabNavItems, tabContentWrapper) => {
    tabNavItems.forEach((tabNavItem) => {
      dom.getFirstByClassName(tabNavItemLinkClass, tabNavItem).addEventListener('click', (event) => {
        onTabNavItemClick(event, tabNavItem, tabNavItems, tabContentWrapper);
      });
    });
  };

  const init = () => {
    tabSections.forEach((tabSection) => {
      if (isTabNavModule(tabSection)) {
        const tabNavItems = dom.getByClassName(tabNavItemClass, tabSection);
        const tabContentWrapper = dom.getFirstByClassName(tabContentWrapperClass, tabSection);
        moveTabContentToWrapper(tabNavItems, tabContentWrapper);
        maybeSelectFirstTab(tabNavItems, tabContentWrapper);
        addEventListeners(tabNavItems, tabContentWrapper);
      }
    });
  };

  return {
    init,
  };
};

const dragOnOverflow = (tabSections) => {
  const breakpoint = 980; // From: assets/src/scss/public/breakpoints.scss.
  let swiperInstances = [];
  let isEnabled = false;

  const meetsCondition = () => {
    const windowWidth = window.innerWidth;
    return windowWidth <= breakpoint;
  };

  const enable = () => {
    if (!isEnabled) {
      tabSections.forEach((tabSection) => {
        swiperInstances.push(new Swiper(tabSection, {
          slidesPerView: 'auto',
          freeMode: true,
          spaceBetween: 8,
        }));
      });
      isEnabled = true;
    }
  };

  const disable = () => {
    if (isEnabled) {
      swiperInstances.forEach((swiperInstance) => {
        swiperInstance.destroy();
      });
      swiperInstances = [];
      isEnabled = false;
    }
  };

  const maybeEnable = () => (meetsCondition() ? enable() : disable());

  return {
    maybeEnable,
  };
};

const tabs = () => {
  const tabSectionClassname = 'js-qusd-tabs-wrapper';
  const tabClassname = 'js-qusd-tab';
  const swiperSlideClass = 'swiper-slide';
  let tabSections = [];
  let tabSectionsFound = false;
  let dragOnOverflowHandler;
  let tabNavigationHandler;

  /*
   * Divi adds `<div>` wrappers around our tabs, which breaks SwiperJS, as it
   * needs elements with the `swiper-slide` to be direct children of the element
   * with the `swiper` class.
   *
   * This function checks if the tab is inside a Divi module, and moves the
   * class if necessary.
   */
  const maybeMoveSwiperClass = (tabSection) => {
    if (divi.isInsideModuleWrapper(tabSection)) {
      const tabElements = dom.getByClassName(tabClassname, tabSection);
      tabElements.forEach((tab) => {
        if (divi.isInsideModuleWrapper(tab)) {
          const tabGrandparent = tab.parentNode.parentNode;
          tab.classList.remove(swiperSlideClass);
          tabGrandparent.classList.add(swiperSlideClass);
        }
      });
    }
  };

  const init = () => {
    tabSections = dom.getByClassName(tabSectionClassname);
    tabSectionsFound = tabSections.length > 0;
    if (tabSectionsFound) {
      tabSections.forEach((tabSection) => maybeMoveSwiperClass(tabSection));
      dragOnOverflowHandler = dragOnOverflow(tabSections);
      dragOnOverflowHandler.maybeEnable();
      tabNavigationHandler = tabNavigation(tabSections);
      tabNavigationHandler.init();
    }
  };

  const onResize = () => {
    if (tabSectionsFound) {
      dragOnOverflowHandler.maybeEnable();
    }
  };

  return {
    init,
    onResize,
  };
};

export default tabs();
